import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  FiCalendar,
  FiMapPin,
  FiClock,
  FiUsers,
  FiX,
  FiSearch,
  FiStar,
  FiTag,
  FiShoppingCart,
  FiChevronRight,
  FiFilter,
  FiMusic,
  FiGlobe,
  FiZap,
  FiTrendingUp,
  FiMinus,
  FiPlus,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import eventsService from '../services/events.service';
import type { EventNew, TicketType, EventCategory as EventCategoryType } from '../services/events.service';
import { Card, LoaderComponent, EmptyState, Modal, Button } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import walletService from '../services/wallet.service';
import type { SavedCardSummary } from '../types/wallet.types';
import { nowCv, fmtParede } from '../utils/tempoCv';

// As datas vêm do MySQL como CVT (naive) com .000Z colado pelo mysql2 (parece UTC
// mas é a hora de parede inserida). Formatar em UTC devolve exatamente o que foi
// inserido (17:00), em vez de tirar 1h como dava o fuso Atlantic/Cape_Verde.
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-CV', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('pt-CV', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toNum = (v: any): number => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const formatCurrency = (value: number | string | undefined | null) => {
  return new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
  }).format(toNum(value));
};

const categoryIcons: Record<string, React.ReactNode> = {
  CONCERT: <FiMusic />,
  FESTIVAL: <FiZap />,
  SPORTS: <FiTrendingUp />,
  PARTY: <FiStar />,
  WORKSHOP: <FiFilter />,
  CONFERENCE: <FiGlobe />,
  EXHIBITION: <FiCalendar />,
  THEATER: <FiStar />,
  GASTRONOMY: <FiTag />,
  OTHER: <FiTag />,
};

const getCategoryLabel = (cat: string): string => {
  // Note: category labels are resolved via i18n in the component
  const labels: Record<string, string> = {
    CONCERT: 'Música',
    FESTIVAL: 'Festival',
    WORKSHOP: 'Workshop',
    CONFERENCE: 'Conferência',
    SPORTS: 'Desporto',
    THEATER: 'Teatro',
    EXHIBITION: 'Exposição',
    PARTY: 'Festa',
    GASTRONOMY: 'Gastronomia',
    OTHER: 'Outros',
  };
  return labels[cat] || cat;
};

// Event Card
const EventCard: React.FC<{ event: EventNew; onClick: () => void }> = ({ event, onClick }) => {
  const { t } = useTranslation();
  const eventDate = new Date(event.data_inicio);
  const isPast = eventDate < nowCv();
  const isSoldOut = (event as any).flag_esgotado === true || (event as any).flag_esgotado === 1
    // A API manda bilhetes_disponiveis como texto ("0"): comparar com === 0 nunca era verdade.
    || (event.bilhetes_disponiveis != null && Number(event.bilhetes_disponiveis) <= 0 && Number(event.total_bilhetes_vendidos || 0) > 0);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-2xl border border-pagali-gray-100 overflow-hidden
                  hover:shadow-lg transition-all group ${isPast ? 'opacity-60' : ''}`}
    >
      {/* Image */}
      <div className="relative h-44 bg-gradient-to-br from-pagali-primary to-pagali-purple overflow-hidden">
        {event.imagem_capa ? (
          <img
            src={event.imagem_capa}
            alt={event.titulo}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FiCalendar className="text-5xl text-white/30" />
          </div>
        )}

        {/* Date Badge */}
        <div className="absolute top-3 left-3 bg-white rounded-lg px-3 py-1.5 shadow-md text-center">
          <p className="text-[10px] font-bold text-pagali-primary uppercase leading-tight">
            {eventDate.toLocaleDateString('pt-CV', { timeZone: 'UTC', month: 'short' })}
          </p>
          <p className="text-xl font-bold text-pagali-gray-800 leading-none">
            {eventDate.toLocaleDateString('pt-CV', { timeZone: 'UTC', day: 'numeric' })}
          </p>
        </div>

        {/* Category Badge */}
        {event.categoria && (
          <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-[11px] font-medium">
            {getCategoryLabel(event.categoria)}
          </div>
        )}

        {/* Price Badge */}
        {event.preco_minimo != null && toNum(event.preco_minimo) > 0 && (
          <div className="absolute bottom-3 right-3 bg-pagali-primary text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
            {toNum(event.preco_minimo) === toNum(event.preco_maximo)
              ? formatCurrency(event.preco_minimo)
              : `Desde ${formatCurrency(event.preco_minimo)}`}
          </div>
        )}
        {event.preco_minimo != null && toNum(event.preco_minimo) === 0 && (
          <div className="absolute bottom-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {t('events.free')}
          </div>
        )}

        {/* Fita de canto ESGOTADO · SOLD OUT (automático: sem bilhetes disponíveis) */}
        {isSoldOut && !isPast && (
          <div className="absolute top-0 left-0 w-24 h-24 overflow-hidden pointer-events-none z-10">
            <div className="absolute -left-12 top-5 w-48 -rotate-45 bg-red-600 text-white text-center text-[9px] font-bold uppercase tracking-wider py-1 shadow-md">
              Esgotado · Sold Out
            </div>
          </div>
        )}
        {isPast && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-white/90 text-pagali-gray-700 px-4 py-2 rounded-full text-sm font-medium">
              {t('events.eventEnded')}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-pagali-gray-800 text-base mb-1.5 group-hover:text-pagali-primary transition-colors line-clamp-2">
          {event.titulo}
        </h3>
        {event.descricao_curta && (
          <p className="text-sm text-pagali-gray-500 line-clamp-2 mb-3">
            {event.descricao_curta}
          </p>
        )}
        <div className="flex items-center gap-3 text-xs text-pagali-gray-400">
          <span className="flex items-center gap-1">
            <FiClock className="text-pagali-primary" />
            {formatTime(event.data_inicio)}
          </span>
          {event.local_nome && (
            <span className="flex items-center gap-1 truncate">
              <FiMapPin className="text-pagali-primary flex-shrink-0" />
              {event.local_cidade ? `${event.local_nome}, ${event.local_cidade}` : event.local_nome}
            </span>
          )}
        </div>
        {/* Tickets info */}
        {event.tipos_bilhetes !== undefined && event.tipos_bilhetes > 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <FiTag className="text-pagali-gray-400" />
            <span className="text-pagali-gray-500">
              {event.tipos_bilhetes} tipo{event.tipos_bilhetes !== 1 ? 's' : ''} de bilhete
            </span>
            {event.bilhetes_disponiveis !== undefined && (
              <span className="text-pagali-gray-400">
                · {event.bilhetes_disponiveis} disponíveis
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

// Main Eventos Page
const Eventos: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, cliente } = useAuth();
  const { showSuccess, showError } = useToast();

  const [events, setEvents] = useState<EventNew[]>([]);
  const [featuredEvents, setFeaturedEvents] = useState<EventNew[]>([]);
  const [categories, setCategories] = useState<EventCategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<EventNew | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  // A ultima falha do checkout. Vive no pai porque e o pai que chama a API, e tem de
  // chegar ao modal — um erro so em toast fica atras do modal e ninguem o ve.
  const [erroCheckout, setErroCheckout] = useState<string | null>(null);
  const [sispPayment, setSispPayment] = useState<{ url: string; orderId: number | string; ref: string } | null>(null);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Pagali Pass');
    loadData();
    // Promotores escondidos temporariamente — não fazer fetch
  }, []);

  useEffect(() => {
    loadEvents();
  }, [selectedCategory, searchQuery]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventsRes, featured, cats] = await Promise.allSettled([
        eventsService.getEvents({ limit: 20 }),
        eventsService.getFeaturedEvents(),
        eventsService.getCategories(),
      ]);

      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data);
      if (featured.status === 'fulfilled') {
        setFeaturedEvents(
          [...featured.value].sort(
            (a, b) => new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime()
          )
        );
      }
      if (cats.status === 'fulfilled') setCategories(cats.value);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('events.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const result = await eventsService.getEvents({
        limit: 20,
        category: selectedCategory || undefined,
        search: searchQuery || undefined,
      });
      setEvents(result.data);
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const handleEventClick = async (event: EventNew) => {
    setSelectedEvent(event);
    setModalOpen(true);
    setDetailLoading(true);
    try {
      const [detail, tickets] = await Promise.all([
        eventsService.getEventDetail(event.id),
        eventsService.getTicketTypes(event.id),
      ]);
      setSelectedEvent(detail);
      setTicketTypes(tickets);
    } catch (err) {
      console.error('Error loading event detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const now = nowCv();
  const upcomingEvents = events.filter((e) => new Date(e.data_inicio) >= now);
  const pastEvents = events.filter((e) => new Date(e.data_inicio) < now);

  if (loading) {
    return <LoaderComponent size="lg" text={t('events.loadingEvents')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-pagali-gray-800">{t('events.title')}</h1>
          <p className="text-pagali-gray-500 text-sm">{t('events.subtitle')}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-pagali-gray-400" />
        <input
          type="text"
          placeholder={t('events.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white rounded-2xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-2 focus:ring-pagali-primary/20 outline-none transition-all shadow-sm text-sm"
        />
      </div>

      {/* Categories Filter */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
              !selectedCategory
                ? 'bg-pagali-primary text-white shadow-sm'
                : 'bg-white text-pagali-gray-600 border border-pagali-gray-200 hover:border-pagali-primary'
            }`}
          >
            <FiGlobe className="text-xs" /> {t('common.all')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.categoria}
              onClick={() => setSelectedCategory(cat.categoria === selectedCategory ? '' : cat.categoria)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                selectedCategory === cat.categoria
                  ? 'bg-pagali-primary text-white shadow-sm'
                  : 'bg-white text-pagali-gray-600 border border-pagali-gray-200 hover:border-pagali-primary'
              }`}
            >
              {categoryIcons[cat.categoria] || <FiTag className="text-xs" />}
              {getCategoryLabel(cat.categoria)}
              <span className="text-[10px] opacity-70">({cat.total})</span>
            </button>
          ))}
        </div>
      )}

      {/* Featured Events */}
      {featuredEvents.length > 0 && !searchQuery && !selectedCategory && (
        <div>
          <h2 className="font-semibold text-pagali-gray-800 mb-3 flex items-center gap-2">
            <FiStar className="text-yellow-500" /> {t('events.featured')}
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
            {featuredEvents.map((event) => (
              <div key={event.id} className="min-w-[300px] max-w-[300px] flex-shrink-0">
                <EventCard event={event} onClick={() => handleEventClick(event)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div>
          <h2 className="font-semibold text-pagali-gray-800 mb-3 flex items-center gap-2">
            <FiCalendar className="text-pagali-primary" /> {t('events.upcoming')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcomingEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => handleEventClick(event)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div>
          <h2 className="font-semibold text-pagali-gray-700 mb-3 flex items-center gap-2">
            <FiClock className="text-pagali-gray-400" /> {t('events.past')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pastEvents.slice(0, 6).map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => handleEventClick(event)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {events.length === 0 && !loading && (
        <Card className="text-center py-16">
          <FiCalendar className="text-5xl text-pagali-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-pagali-gray-700 mb-2">
            {searchQuery || selectedCategory ? t('events.noEventsFound') : t('events.noEventsAvailable')}
          </h3>
          <p className="text-pagali-gray-500 max-w-md mx-auto">
            {searchQuery
              ? t('events.noEventsForSearch', { query: searchQuery })
              : t('events.noEventsPublished')}
          </p>
          {(searchQuery || selectedCategory) && (
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory(''); }}
              className="mt-4 text-pagali-primary font-medium hover:underline"
            >
              {t('common.clearFilters')}
            </button>
          )}
        </Card>
      )}

      {/* Event Detail Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedEvent(null); setTicketTypes([]); setErroCheckout(null); }}
        title={selectedEvent?.titulo || 'Evento'}
        size="lg"
      >
        {selectedEvent && (
          <EventDetailContent
            event={selectedEvent}
            ticketTypes={ticketTypes}
            loading={detailLoading}
            erro={erroCheckout}
            onCheckout={async (items, method) => {
              setErroCheckout(null);
              try {
                // 1. Criar pedido
                const result = await eventsService.checkout(selectedEvent.id, {
                  items,
                  comprador_nome: cliente?.NOME || user?.name || '',
                  comprador_email: cliente?.EMAIL || user?.email || '',
                  // Um cartao guardado e um pagamento por cartao: o pedido nasce SISP e a
                  // rota de pagamento reescreve para SISP_TOKEN. Enviar `token:<uuid>` aqui
                  // gravava no pedido um valor que nao e um metodo de pagamento.
                  metodo_pagamento: method.startsWith('token:') ? 'SISP' : method,
                });

                // 2. Se o pedido for grátis ou já veio confirmado
                if (result.is_free || result.pagamento_status === 'PAID' || result.status === 'CONFIRMED') {
                  showSuccess(t('events.orderCreated', { ref: result.codigo }));
                  setModalOpen(false);
                  return;
                }

                const orderId = result.pedido_id || result.codigo;

                // 3. Pagar conforme método escolhido
                if (method.startsWith('token:')) {
                  // Cartao guardado: UMA chamada, que cobra e confirma o pedido na mesma
                  // transaccao. Nao ha iframe nem segunda rota — e a diferenca entre isto
                  // e o SISP, onde o cliente tem de introduzir os dados.
                  await eventsService.payOrder(orderId, 'TOKEN', {
                    token_id: method.slice('token:'.length),
                  });
                  // E DIZER que pagou. Este ramo nao mostrava nada nem fechava o modal: a
                  // Linda clicou tres vezes e criou tres pedidos, porque do lado dela «nada
                  // acontecia». Um pagamento sem confirmacao visivel e indistinguivel de um
                  // botao que nao funciona.
                  showSuccess(t('events.orderCreated', { ref: result.codigo }));
                  setModalOpen(false);
                  navigate('/meus-bilhetes');
                } else if (method === 'SISP') {
                  // Chamar backend para obter paymentUrl, depois abrir num iframe modal
                  const payResult = await eventsService.payOrder(orderId, 'SISP', {
                    returnUrl: `${window.location.origin}/events?orderId=${orderId}`,
                  });
                  if (!payResult?.paymentUrl) {
                    showError('Não foi possível iniciar o pagamento SISP');
                    return;
                  }
                  // Fechar o modal de checkout e abrir o modal de pagamento SISP
                  setModalOpen(false);
                  setSispPayment({
                    url: payResult.paymentUrl,
                    orderId,
                    ref: result.codigo,
                  });
                } else {
                  // WALLET — pagamento imediato
                  await eventsService.payOrder(orderId, 'WALLET');
                  showSuccess(t('events.orderCreated', { ref: result.codigo }));
                  setModalOpen(false);
                }
              } catch (err) {
                // O toast e z-50 e o modal cobre-o: um erro so em toast era invisivel com o
                // modal aberto, e foi por isso que ate a recusa do cartao pareceu «nada
                // acontece». Mostrar DENTRO do modal, e manter o toast para quem o veja.
                const msg = err instanceof Error ? err.message : t('events.checkoutError');
                setErroCheckout(msg);
                showError(msg);
              }
            }}
          />
        )}
      </Modal>

      {/* SISP Payment Modal (iframe) */}
      {sispPayment && (
        <SispPaymentModal
          url={sispPayment.url}
          orderId={sispPayment.orderId}
          orderRef={sispPayment.ref}
          onClose={() => setSispPayment(null)}
          onSuccess={(ref) => {
            setSispPayment(null);
            showSuccess(t('events.orderCreated', { ref }));
          }}
          onFailed={(msg) => {
            setSispPayment(null);
            showError(msg || 'Pagamento não concluído');
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SISP payment iframe modal
// ─────────────────────────────────────────────────────────────
interface SispModalProps {
  url: string;
  orderId: number | string;
  orderRef: string;
  onClose: () => void;
  onSuccess: (ref: string) => void;
  onFailed: (msg?: string) => void;
}

const SispPaymentModal: React.FC<SispModalProps> = ({ url, orderId, orderRef, onClose, onSuccess, onFailed }) => {
  React.useEffect(() => {
    // 1. Listener para postMessage vindo do iframe (callback SISP)
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'sisp-result') return;
      if (data.status === 'success') onSuccess(orderRef);
      else onFailed(data.error);
    };
    window.addEventListener('message', onMessage);

    // 2. Polling: verificar estado do pedido a cada 3 segundos (fallback se postMessage falhar)
    let attempts = 0;
    const maxAttempts = 200;
    const pollInterval = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollInterval);
        onFailed('Tempo limite excedido');
        return;
      }
      try {
        const order = await eventsService.getOrder(orderId);
        if (order?.pagamento_status === 'PAID' || order?.status === 'CONFIRMED') {
          clearInterval(pollInterval);
          onSuccess(orderRef);
        } else if (order?.status === 'CANCELLED' || order?.pagamento_status === 'FAILED') {
          clearInterval(pollInterval);
          onFailed('Pagamento cancelado ou falhou');
        }
      } catch { /* ignore */ }
    }, 3000);

    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(pollInterval);
    };
  }, [orderId, orderRef, onSuccess, onFailed]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 520, height: '90vh', maxHeight: 720,
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,.3)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #eee',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fafafa',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
            🔒 Pagamento seguro · Vinti4
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              width: 32, height: 32, border: 0, borderRadius: 8,
              background: '#f0f0f0', cursor: 'pointer', fontSize: 18,
              color: '#666',
            }}
          >
            ✕
          </button>
        </div>
        <iframe
          src={url}
          style={{ flex: 1, width: '100%', border: 0 }}
          title="Pagamento SISP"
          allow="payment *"
        />
      </div>
    </div>
  );
};

/**
 * Os metodos de pagamento de um bilhete.
 *
 * `token:<id>` e um cartao guardado concreto — o id vai no proprio valor porque o
 * utilizador pode ter varios, e cada um e uma opcao distinta no ecra.
 */
type PaymentMethod = 'WALLET' | 'SISP' | `token:${string}`;

// Event Detail Content inside Modal
const EventDetailContent: React.FC<{
  event: EventNew;
  ticketTypes: TicketType[];
  loading: boolean;
  onCheckout: (items: { ticket_type_id: number; quantidade: number }[], method: PaymentMethod) => void;
  /** A ultima falha do pagamento, para se ver sem fechar o modal. */
  erro?: string | null;
}> = ({ event, ticketTypes, loading, onCheckout, erro }) => {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('WALLET');
  // Os cartoes guardados deste utilizador. Vazio nao e erro: quem nao tokenizou nenhum
  // cartao continua a pagar por carteira ou cartao novo.
  const [savedCards, setSavedCards] = useState<SavedCardSummary[]>([]);

  useEffect(() => {
    let vivo = true;
    walletService.getCards()
      .then((cards) => { if (vivo) setSavedCards(cards); })
      // Nao bloqueia a compra: sem a lista ainda se paga pelos outros metodos, e um
      // checkout que nao abre por causa disto seria pior do que nao oferecer a opcao.
      .catch(() => { if (vivo) setSavedCards([]); });
    return () => { vivo = false; };
  }, []);
  const [conferenceInfo, setConferenceInfo] = useState<{
    callForPapersEnabled?: boolean;
    scholarshipEnabled?: boolean;
    callForPapersDeadline?: string;
    scholarshipDeadline?: string;
    conferenceUrl?: string;
  } | null>(null);

  const isPast = new Date(event.data_inicio) < nowCv();
  const total = ticketTypes.reduce(
    (sum, tt) => sum + (quantities[tt.id] || 0) * tt.preco,
    0
  );
  const hasItems = Object.values(quantities).some((q) => q > 0);

  const updateQty = (id: number, delta: number, max: number) => {
    setQuantities((prev) => {
      const current = prev[id] || 0;
      const next = Math.max(0, Math.min(current + delta, max));
      return { ...prev, [id]: next };
    });
  };

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
    fetch(`${apiUrl}/v1/conference/${event.id}/public`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setConferenceInfo(data); })
      .catch(() => {});
  }, [event.id]);

  const formatDeadline = (d?: string) => {
    if (!d) return null;
    return fmtParede(d, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) return <LoaderComponent size="sm" />;

  return (
    <div className="space-y-5">
      {/* Banner */}
      {event.imagem_capa && (
        <div className="relative rounded-xl overflow-hidden h-48">
          <img src={event.imagem_capa} alt={event.titulo} className="w-full h-full object-cover" />
          {/* O detalhe da API traz bilhetes_disponiveis=null: o esgotado automático
              decide-se pelos tipos de bilhete (todos esgotados), como no events.cv. */}
          {((event as any).flag_esgotado === true || (event as any).flag_esgotado === 1
            || (ticketTypes.length > 0 && ticketTypes.every((tt) => tt.estado_venda === 'ESGOTADO' || Number(tt.quantidade_disponivel) <= 0))) && (
            <div className="absolute top-0 left-0 w-28 h-28 overflow-hidden pointer-events-none z-10">
              <div className="absolute -left-14 top-6 w-56 -rotate-45 bg-red-600 text-white text-center text-xs font-bold uppercase tracking-wider py-1.5 shadow-md">
                Esgotado · Sold Out
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 text-sm text-pagali-gray-600">
          <FiCalendar className="text-pagali-primary flex-shrink-0" />
          <span>{formatDate(event.data_inicio)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-pagali-gray-600">
          <FiClock className="text-pagali-primary flex-shrink-0" />
          <span>{formatTime(event.data_inicio)}{event.data_fim ? ` - ${formatTime(event.data_fim)}` : ''}</span>
        </div>
        {event.local_nome && (
          <div className="flex items-center gap-2 text-sm text-pagali-gray-600 col-span-2">
            <FiMapPin className="text-pagali-primary flex-shrink-0" />
            <span>{event.local_nome}{event.local_cidade ? `, ${event.local_cidade}` : ''}</span>
          </div>
        )}
        {event.local_capacidade && (
          <div className="flex items-center gap-2 text-sm text-pagali-gray-600">
            <FiUsers className="text-pagali-primary flex-shrink-0" />
            <span>{t('events.capacity')}: {event.local_capacidade}</span>
          </div>
        )}
        {(event.idade_minima ?? 0) > 0 && (
          <div className="col-span-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
              🔞 Idade mínima: {event.idade_minima}+
            </span>
          </div>
        )}
      </div>

      {/* YouTube Video */}
      {event.video_url && (() => {
        const m = event.video_url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
        const videoId = m?.[1];
        return videoId ? (
          <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title="Vídeo do evento"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        ) : null;
      })()}

      {/* Description */}
      {(event.descricao || event.descricao_curta) && (
        <div>
          <p className="text-sm text-pagali-gray-600 whitespace-pre-line">
            {event.descricao || event.descricao_curta}
          </p>
        </div>
      )}

      {/* Conference section */}
      {conferenceInfo && (conferenceInfo.callForPapersEnabled || conferenceInfo.scholarshipEnabled) && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Conferência</p>
          <div className="space-y-2">
            {conferenceInfo.callForPapersEnabled && (
              <a
                href={`${process.env.REACT_APP_EVENTS_URL || 'https://events.cv'}/conference/${event.id}/call-for-papers`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                <span>Submeter Trabalho</span>
                <span className="flex items-center gap-2 text-xs text-indigo-200">
                  {conferenceInfo.callForPapersDeadline && (
                    <span>até {formatDeadline(conferenceInfo.callForPapersDeadline)}</span>
                  )}
                  <FiChevronRight />
                </span>
              </a>
            )}
            {conferenceInfo.scholarshipEnabled && (
              <a
                href={`${process.env.REACT_APP_EVENTS_URL || 'https://events.cv'}/conference/${event.id}/bolsas`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
              >
                <span>Candidatar a Bolsa</span>
                <span className="flex items-center gap-2 text-xs text-emerald-200">
                  {conferenceInfo.scholarshipDeadline && (
                    <span>até {formatDeadline(conferenceInfo.scholarshipDeadline)}</span>
                  )}
                  <FiChevronRight />
                </span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Map */}
      {(event.local_nome || event.local_cidade) && (() => {
        const mapsKey = 'AIzaSyD35pZFnJYKEXKJ_XIC8aVG7OkeQNmJpn0';
        const hasCoords = event.local_latitude && event.local_longitude;
        const embedQ = hasCoords
          ? `${event.local_latitude},${event.local_longitude}`
          : encodeURIComponent([event.local_endereco || event.local_nome, event.local_cidade, 'Cabo Verde'].filter(Boolean).join(', '));
        const query = hasCoords
          ? `${event.local_latitude},${event.local_longitude}`
          : encodeURIComponent([event.local_endereco || event.local_nome, event.local_cidade, 'Cabo Verde'].filter(Boolean).join(', '));
        return (
          <div className="rounded-xl overflow-hidden border border-pagali-gray-100">
            <iframe
              title="Localização do evento"
              src={`https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=${embedQ}&language=pt&zoom=16`}
              className="w-full border-0"
              style={{ height: 220 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="flex items-center justify-between px-3 py-2 bg-white">
              <div>
                <p className="text-xs font-semibold text-pagali-gray-800">{event.local_nome}</p>
                {event.local_cidade && <p className="text-xs text-pagali-gray-500">{event.local_cidade}</p>}
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${query}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-pagali-primary font-semibold hover:underline"
              >
                Abrir no Maps →
              </a>
            </div>
          </div>
        );
      })()}

      {/* Ticket Types */}
      {!isPast && ticketTypes.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-pagali-gray-800">{t('events.tickets')}</h4>
            <span className="text-xs text-pagali-gray-400">{ticketTypes.length} tipo{ticketTypes.length !== 1 ? 's' : ''}</span>
          </div>

          <div className={`space-y-2 ${ticketTypes.length > 4 ? 'max-h-72 overflow-y-auto pr-1' : ''}`}>
            {ticketTypes.map((tt) => {
              const available = tt.quantidade_disponivel;
              const qty = quantities[tt.id] || 0;
              // Estado do servidor (respeita janela do evento + do tipo). O bilhete
              // mostra-se sempre; só se compra quando compravel !== false.
              const emBreve = tt.estado_venda === 'EM_BREVE';
              const encerrado = tt.estado_venda === 'ENCERRADO';
              const isSoldOut = tt.estado_venda === 'ESGOTADO' || available <= 0;
              const naoCompravel = tt.compravel === false || emBreve || encerrado || isSoldOut;
              const isFree = tt.preco === 0;
              const hasDiscount = tt.preco_original && tt.preco_original > tt.preco;
              const discountPct = hasDiscount ? Math.round((1 - tt.preco / tt.preco_original!) * 100) : 0;

              return (
                <div
                  key={tt.id}
                  className={`rounded-xl border transition-all ${
                    naoCompravel
                      ? 'border-pagali-gray-100 bg-pagali-gray-50 opacity-60'
                      : qty > 0
                      ? 'border-pagali-primary bg-pagali-primary/5 shadow-sm'
                      : 'border-pagali-gray-100 bg-white hover:border-pagali-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 px-3 py-3">
                    {/* Selection indicator */}
                    <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 transition-all ${
                      qty > 0 ? 'border-pagali-primary bg-pagali-primary' : 'border-pagali-gray-300'
                    }`}>
                      {qty > 0 && <div className="w-full h-full rounded-full scale-50 bg-white" />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-pagali-gray-800 text-sm leading-tight">{tt.nome}</p>
                        {isFree && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200">
                            GRATUITO
                          </span>
                        )}
                        {hasDiscount && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 border border-orange-200">
                            -{discountPct}%
                          </span>
                        )}
                        {isSoldOut && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-red-100 text-red-600 border border-red-200">
                            ESGOTADO
                          </span>
                        )}
                        {emBreve && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 border border-amber-200">
                            EM BREVE
                          </span>
                        )}
                        {encerrado && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-pagali-gray-100 text-pagali-gray-500 border border-pagali-gray-200">
                            ESGOTADO
                          </span>
                        )}
                      </div>

                      {tt.descricao && (
                        <p className="text-[11px] text-pagali-gray-500 mt-0.5 line-clamp-1">{tt.descricao}</p>
                      )}

                      {tt.beneficios && tt.beneficios.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {tt.beneficios.slice(0, 3).map((b, i) => (
                            <span key={i} className="text-[10px] bg-pagali-gray-100 text-pagali-gray-500 px-1.5 py-0.5 rounded-full">
                              {b}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-baseline gap-2 mt-1">
                        {!isFree && (
                          <span className="font-bold text-pagali-primary text-sm">
                            {formatCurrency(tt.preco)}
                          </span>
                        )}
                        {hasDiscount && (
                          <span className="text-xs text-pagali-gray-400 line-through">
                            {formatCurrency(tt.preco_original!)}
                          </span>
                        )}
                        {!isSoldOut && available <= 10 && (
                          <span className="text-[10px] text-orange-500 font-medium">
                            {available === 1 ? 'Último!' : `${available} restantes`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quantity selector — só quando comprável (dentro da janela de venda) */}
                    {!naoCompravel && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => updateQty(tt.id, -1, tt.max_por_compra)}
                          disabled={qty <= 0}
                          className="w-7 h-7 rounded-lg bg-pagali-gray-100 hover:bg-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 disabled:opacity-30 transition-colors"
                        >
                          <FiMinus size={12} />
                        </button>
                        <span className="w-7 text-center text-sm font-bold text-pagali-gray-800">{qty}</span>
                        <button
                          onClick={() => updateQty(tt.id, 1, Math.min(tt.max_por_compra, available))}
                          disabled={qty >= Math.min(tt.max_por_compra, available)}
                          className="w-7 h-7 rounded-lg bg-pagali-primary/10 hover:bg-pagali-primary/20 flex items-center justify-center text-pagali-primary disabled:opacity-30 transition-colors"
                        >
                          <FiPlus size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Checkout */}
          {hasItems && (
            <div className="mt-4 rounded-xl border border-pagali-primary/20 bg-pagali-primary/5 overflow-hidden">
              {/* Order summary */}
              <div className="px-4 py-3 space-y-1.5 border-b border-pagali-primary/10">
                {ticketTypes
                  .filter(tt => (quantities[tt.id] || 0) > 0)
                  .map(tt => (
                    <div key={tt.id} className="flex items-center justify-between text-sm">
                      <span className="text-pagali-gray-600">
                        <span className="font-medium text-pagali-gray-800">{quantities[tt.id]}×</span>{' '}
                        {tt.nome}
                      </span>
                      <span className="font-medium text-pagali-gray-800">
                        {tt.preco === 0 ? 'Grátis' : formatCurrency((quantities[tt.id] || 0) * tt.preco)}
                      </span>
                    </div>
                  ))}
                <div className="flex items-center justify-between pt-1.5 border-t border-pagali-primary/10">
                  <span className="text-sm font-semibold text-pagali-gray-700">Total</span>
                  <span className="text-lg font-bold text-pagali-primary">
                    {total === 0 ? 'Gratuito' : formatCurrency(total)}
                  </span>
                </div>
              </div>

              {/* Payment method (only for paid) */}
              {total > 0 && (
                <div className="px-4 pt-3 pb-1">
                  <p className="text-xs font-medium text-pagali-gray-500 mb-2">Método de pagamento</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'WALLET', label: 'Wallet Pagali', icon: '💳' },
                      // Os cartoes guardados vem ANTES do cartao novo: quem tem um
                      // guardado quer usa-lo, e po-los depois obriga a passar ao lado.
                      ...savedCards.map((c) => ({
                        value: `token:${c.id}` as PaymentMethod,
                        label: c.alias
                          || `${c.cardType || 'Cartão'} •••• ${c.lastFourDigits}`,
                        icon: '💳',
                      })),
                      { value: 'SISP',   label: savedCards.length ? 'Outro cartão' : 'Cartão Vinti4', icon: '🏧' },
                    ].map(m => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setPaymentMethod(m.value as PaymentMethod)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                          paymentMethod === m.value
                            ? 'border-pagali-primary bg-white text-pagali-primary shadow-sm'
                            : 'border-pagali-gray-200 bg-white/60 text-pagali-gray-500 hover:border-pagali-gray-300'
                        }`}
                      >
                        <span>{m.icon}</span> {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {erro && (
                <div className="mx-4 mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
                  <p className="text-xs font-semibold text-red-800">Pagamento não concluído</p>
                  <p className="text-xs text-red-700 mt-0.5">{erro}</p>
                </div>
              )}

              <div className="px-4 pb-4 pt-3">
                <Button
                  onClick={() => {
                    const items = Object.entries(quantities)
                      .filter(([, q]) => q > 0)
                      .map(([id, q]) => ({ ticket_type_id: Number(id), quantidade: q }));
                    onCheckout(items, paymentMethod);
                  }}
                  fullWidth
                >
                  <FiShoppingCart className="mr-2" />
                  {total === 0 ? 'Confirmar inscrição gratuita' : t('events.buyTickets')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Past event notice */}
      {isPast && (
        <div className="text-center p-4 bg-pagali-gray-50 rounded-xl">
          <p className="text-pagali-gray-500">{t('events.eventEndedNotice')}</p>
        </div>
      )}

      {/* No ticket types but event is active */}
      {!isPast && ticketTypes.length === 0 && !loading && (
        <div className="text-center p-4 bg-blue-50 rounded-xl">
          <p className="text-blue-600 text-sm">{t('events.ticketsSoonAvailable')}</p>
        </div>
      )}
    </div>
  );
};

export default Eventos;
